import { useMemo } from "react";
import { Flex, Heading, View, Text } from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import ContactIcon from "../../../assets/contactIcon.svg";
import StarIcon from "../../../assets/starIcon.svg";
import Emitter from "../../../emitter";
import {
  API_CALL_DIAL,
  CALL,
  noActiveHistory,
} from "../../../constants/constants";
import storeRTK from "../../../store/storeRTK";
import { setSelectedTab } from "../../../actions/common";
import { RootState } from "../../../store/storeConfig";
import { fixJson, formatTimestamp, getInitialsAndColor } from "../../../utils";
import HistoryCallButton from "../../common/HistoryCallButton/HistoryCallButton";
import { formatLabel } from "../../../utils/utils";
import ActiveCallContainer from "../../CallTab/CallDialer/ActiveCallContainer";
import { useCustomerAttributes } from "../../../hooks/useCustomerAttributes";

type Props = {
  activeCallDetail: any;
  type: "call" | "callback" | "voicemail";
};

const HistoryDetail = ({ activeCallDetail, type }: Props) => {
  const agent = useSelector((state: RootState) => state.agent);
  const customerAttributes = activeCallDetail?.customer?.attributes || {};
  const agentBusinessAttributes = agent?.businessAttributes || [];

  const { initials, backgroundColor } = useMemo(() => {
    if (type === CALL) {
      return getInitialsAndColor("N A") as {
        initials: string;
        backgroundColor: string;
      };
    } else {
      const firstName = customerAttributes?.FIRSTNAME?.[0] || "";
      const lastName = customerAttributes?.LASTNAME?.[0] || "";
      return getInitialsAndColor(`${firstName} ${lastName}`) as {
        initials: string;
        backgroundColor: string;
      };
    }
  }, [activeCallDetail?.id]);

  const dialCall = (phoneNumber: string) => {
    Emitter.emit(API_CALL_DIAL, { phoneNumber });
    storeRTK.dispatch(setSelectedTab("Call"));
  };

  const { groupedAttributes } = useCustomerAttributes({
    customerAttrs: customerAttributes,
    businessAttrs: agentBusinessAttributes,
    type: "detail",
  });

  const phoneNumber =
    customerAttributes?.Customer_ANI ||
    customerAttributes?.PHONE_NUMBER ||
    activeCallDetail?.customer?.displayName ||
    activeCallDetail?.customer?.phoneNumber ||
    "";

  return activeCallDetail?.id ? (
    <View
      borderColor="gray-200"
      borderWidth="thin"
      height="100%"
      backgroundColor="gray-100"
      UNSAFE_className="call-history-detail"
    >
      <View
        padding="10px 15px"
        borderBottomWidth="thin"
        borderBottomColor="gray-200"
      >
        <Flex
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex alignItems="center">
            <Flex
              justifyContent="center"
              alignItems="center"
              UNSAFE_className="agent-avatar"
              UNSAFE_style={{ backgroundColor, borderRadius: "50%" }}
            >
              <Text UNSAFE_className="call-monitor-agent-text">{initials}</Text>
            </Flex>
            <View marginX={12}>
              <Heading level={3} margin={0}>
                <Text UNSAFE_className="call-history-detail-title">
                  {phoneNumber || "-"}
                </Text>
              </Heading>
            </View>
          </Flex>
          <Flex direction="row" alignItems="center" gap="size-200">
            <button
              className="call-history-action-btn"
              aria-label="Add to favourites"
              data-testid="add-favourites-button"
              disabled
            >
              <img src={StarIcon} alt="favourite icon" />
              <Text marginX={7} UNSAFE_style={{ whiteSpace: "nowrap" }}>
                Add to favourites
              </Text>
            </button>
            <button
              className="call-history-action-btn"
              aria-label="Add to speed dials"
              data-testid="add-speed-dials-button"
              disabled
            >
              <img src={ContactIcon} alt="contact icon" />
              <Text marginX={7} UNSAFE_style={{ whiteSpace: "nowrap" }}>
                Add to speed dials
              </Text>
            </button>
            <HistoryCallButton onClick={() => dialCall(phoneNumber)} />
          </Flex>
        </Flex>
      </View>

      <View>
        <Flex direction="row" height="100%">
          <Flex width="100%">
            <View UNSAFE_className="call-history-detail-body" width="100%">
              {groupedAttributes.length > 0 ? (
                groupedAttributes.map((group, i) => (
                  <View key={`group-${i}`} marginTop={23}>
                    <Flex direction="row" wrap="wrap" gap={20}>
                      {group.map((attr, index) => (
                        <Flex
                          direction="column"
                          key={`attr-${i + index}`}
                          minWidth="30%"
                        >
                          <Text UNSAFE_className="call-detail-title">
                            {attr.attributeName.toLowerCase() === "notes"
                              ? "Transfer Notes"
                              : formatLabel(attr.attributeName)}
                          </Text>
                          {formatLabel(attr.attributeName) ===
                          "Aic Web Contact Monitored History" ? (
                            <Text
                              UNSAFE_className="call-detail-description"
                              UNSAFE_style={{
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                              }}
                            >
                              {fixJson(
                                customerAttributes[attr.attributeName]
                              ).map((detail: any, idx: number) => (
                                <Flex
                                  justifyContent={"space-between"}
                                  marginTop={4}
                                  key={idx}
                                >
                                  <Text>{detail.user}</Text>
                                  <Text>{formatTimestamp(detail.TS)}</Text>
                                </Flex>
                              ))}
                            </Text>
                          ) : (
                            <Text
                              UNSAFE_className="call-detail-description"
                              UNSAFE_style={{
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                              }}
                            >
                              {customerAttributes[attr.attributeName]}
                            </Text>
                          )}
                        </Flex>
                      ))}
                    </Flex>
                  </View>
                ))
              ) : (
                <Flex marginTop={23} justifyContent="center">
                  <Text>No Detail Found</Text>
                </Flex>
              )}
            </View>
          </Flex>
        </Flex>
      </View>
    </View>
  ) : (
    <Flex alignItems="center" justifyContent="center" height="60%" width="100%">
      <ActiveCallContainer
        title={noActiveHistory[type].title}
        description={noActiveHistory[type].description}
      />
    </Flex>
  );
};

export default HistoryDetail;
